// 화면 그리기(§9.8) — 문서 모델과 연산.
//
// 🔴 이 파일은 **DOM 을 모른다**(순수 로직). 뷰어의 겹침 캔버스와 원격지 통합앱 그림판 창이 같은 코드를 쓰고,
//    Node 내장 시험(`node --test test/`)이 그대로 돈다. DOM·React 가 필요한 것은 render.ts·useDrawing.ts 로.
//
// 좌표: 캡처 중인 원격 모니터 기준 **0~1 비율**(x, y). 창 크기·해상도·배율이 달라도 같은 자리에 그려진다.
// 굵기·글자 크기: **화면 높이 1080 기준 px**(`REF_HEIGHT`) — 그릴 때 실제 높이에 비례시킨다.
//
// 연산은 모두 `apply()` 한 곳을 지나고, 되돌리기에 쓸 **역연산**을 돌려준다. 그리는 중인 획은
// `begin → pts… → end` 로 실시간 전송(§9.8.3)되므로 모델도 같은 세 단계를 안다.

/** 굵기·글자 크기의 기준 화면 높이(px). */
export const REF_HEIGHT = 1080;

/** 그려지는 층 — 원격 화면 위 / 흰 판 위. 두 층의 그림은 따로 보존된다(§9.8.3). */
export type Layer = 'screen' | 'board';

/** 작성자 — 뷰어(v) / 원격지(r). id 접두사이기도 하다(양쪽이 동시에 만들어도 겹치지 않는다). */
export type Author = 'v' | 'r';

/** 객체 공통 속성. */
interface Base {
  id: string;
  by: Author;
  layer: Layer;
  color: string;
  /** 선 굵기(기준 높이 px). 글자는 글자 크기. */
  width: number;
}

/** 자유 곡선 — 펜·형광펜. `pts` = [x0, y0, x1, y1, …] 평평한 배열(메시지 크기를 줄인다). */
export interface StrokeObj extends Base {
  kind: 'pen' | 'marker';
  pts: number[];
}

/** 두 점으로 정해지는 도형 — 직선·화살표(시작 a → 끝 b) · 사각형·원(대각선 a–b). */
export interface ShapeObj extends Base {
  kind: 'line' | 'arrow' | 'rect' | 'ellipse';
  a: [number, number];
  b: [number, number];
  /** 사각형·원 채우기(반투명). 선에는 쓰지 않는다. */
  fill?: boolean;
}

/** 글자 — 왼쪽 위 기준점 `at`, 줄바꿈 허용. `width` = 글자 크기. */
export interface TextObj extends Base {
  kind: 'text';
  at: [number, number];
  text: string;
}

export type DrawObj = StrokeObj | ShapeObj | TextObj;
export type ObjKind = DrawObj['kind'];

/** 객체의 일부 속성 바꾸기(`upd`) — 이동·크기·색·글자 내용. id·kind·by 는 바꿀 수 없다. */
export type ObjPatch = Partial<
  Pick<StrokeObj, 'pts'> & Pick<ShapeObj, 'a' | 'b' | 'fill'> & Pick<TextObj, 'at' | 'text'> & Pick<Base, 'color' | 'width'>
>;

/** 종류별로 `upd` 가 바꿀 수 있는 속성 — 나머지 키는 무시한다(상대가 보낸 patch 를 그대로 믿지 않는다). */
const PATCH_KEYS: Record<ObjKind, readonly (keyof ObjPatch)[]> = {
  pen: ['pts', 'color', 'width'],
  marker: ['pts', 'color', 'width'],
  line: ['a', 'b', 'color', 'width'],
  arrow: ['a', 'b', 'color', 'width'],
  rect: ['a', 'b', 'fill', 'color', 'width'],
  ellipse: ['a', 'b', 'fill', 'color', 'width'],
  text: ['at', 'text', 'color', 'width'],
};

/** 문서 연산 — 채널 메시지(`protocol.ts`)와 1:1 이다. */
export type Op =
  | { t: 'add'; objs: DrawObj[] }
  | { t: 'del'; ids: string[] }
  | { t: 'upd'; id: string; patch: ObjPatch }
  | { t: 'clear'; layer: Layer }
  | { t: 'begin'; obj: StrokeObj }
  | { t: 'pts'; id: string; p: number[] }
  | { t: 'end'; id: string }
  | { t: 'board'; on: boolean };

/** 문서 상한(§9.8.3). 넘으면 그 연산만 거절한다(문서를 망가뜨리지 않는다). */
export const LIMITS = {
  maxObjects: 2000,
  /**
   * 획 하나의 점 개수(좌표 쌍). 🔴 객체 하나가 **메시지 한 건(64KB)** 에 들어가야 한다 — 좌표 하나가 `0.1234,` 7바이트라
   * 3000쌍 ≈ 42KB. 5000 이면 70KB 로 넘쳐 그 획이 상대에게 영영 가지 않는다(나눌 수 없는 단위다).
   */
  maxPoints: 3000,
  maxTextChars: 500,
} as const;

/** 깊은 복사(객체는 작고 평평하다 — 배열만 새로 만든다). 문서 밖으로 내보내는 값은 항상 복사본이다. */
export function cloneObj<T extends DrawObj>(o: T): T {
  const c = { ...o } as T;
  if ('pts' in c) (c as StrokeObj).pts = [...(c as StrokeObj).pts];
  if ('a' in c) {
    (c as ShapeObj).a = [...(c as ShapeObj).a] as [number, number];
    (c as ShapeObj).b = [...(c as ShapeObj).b] as [number, number];
  }
  if ('at' in c) (c as TextObj).at = [...(c as TextObj).at] as [number, number];
  return c;
}

/** 좌표 정규화 — 소수 4자리(4K 화면에서도 0.4px). 메시지·문서에 같은 값이 남게 한 곳에서 자른다. */
export function q(v: number): number {
  return Math.round(Math.min(1, Math.max(0, v)) * 10000) / 10000;
}

/**
 * 그림 문서. 순서 = 그려진 순서(나중 것이 위).
 * ⚠️ 삭제 후 되살린 객체는 **맨 위**로 돌아온다(원래 층 순서를 보존하지 않는다 — 겹친 그림이 드물어 단순함을 택했다).
 */
export class DrawDoc {
  private objs = new Map<string, DrawObj>();
  /** 흰 판 켜짐(§9.8.3). */
  board = false;
  /** 바뀔 때마다 증가 — 화면이 다시 그릴지 판단한다. */
  rev = 0;

  /** 보이는 층의 객체(그리기 순서). */
  visible(): DrawObj[] {
    const layer: Layer = this.board ? 'board' : 'screen';
    return [...this.objs.values()].filter((o) => o.layer === layer);
  }

  all(): DrawObj[] {
    return [...this.objs.values()];
  }

  get(id: string): DrawObj | undefined {
    return this.objs.get(id);
  }

  get size(): number {
    return this.objs.size;
  }

  /**
   * 연산 적용 → 역연산(되돌리기용). 적용할 수 없으면(상한·없는 id·규약 밖) `null` 이고 문서는 그대로다.
   * `begin`·`pts` 는 null(획 도중), `end` 가 **획 전체**의 역연산(`del`)을 돌려준다 — 되돌리기는 획 한 번이 한 단위다.
   */
  apply(op: Op): Op | null {
    const inv = this.applyInner(op);
    if (inv !== undefined) {
      this.rev++;
    }
    return inv ?? null;
  }

  /** undefined = 적용 안 됨(문서 불변). null = 적용됐지만 역연산 없음. */
  private applyInner(op: Op): Op | null | undefined {
    switch (op.t) {
      case 'add': {
        const fresh = op.objs.filter((o) => validObj(o) && !this.objs.has(o.id));
        if (fresh.length === 0 || this.objs.size + fresh.length > LIMITS.maxObjects) {
          return undefined;
        }
        for (const o of fresh) this.objs.set(o.id, cloneObj(o));
        return { t: 'del', ids: fresh.map((o) => o.id) };
      }
      case 'del': {
        const gone = op.ids.map((id) => this.objs.get(id)).filter((o): o is DrawObj => !!o);
        if (gone.length === 0) {
          return undefined;
        }
        for (const o of gone) this.objs.delete(o.id);
        return { t: 'add', objs: gone };
      }
      case 'upd': {
        const o = this.objs.get(op.id);
        if (!o) {
          return undefined;
        }
        const prev: Record<string, unknown> = {};
        const next = cloneObj(o) as unknown as Record<string, unknown>;
        const allowed = PATCH_KEYS[o.kind];
        for (const k of Object.keys(op.patch) as (keyof ObjPatch)[]) {
          if (!allowed.includes(k)) continue; // 그 종류에 없는 속성(예: 선의 text)은 무시
          // 없던 채우기는 "꺼짐" 으로 기억한다 — undefined 는 JSON 으로 가면 사라져 역연산이 빈 값이 된다.
          prev[k] = (o as unknown as Record<string, unknown>)[k] ?? (k === 'fill' ? false : undefined);
          next[k] = op.patch[k];
        }
        if (Object.keys(prev).length === 0 || !validObj(next)) {
          return undefined;
        }
        this.objs.set(o.id, cloneObj(next as unknown as DrawObj));
        return { t: 'upd', id: o.id, patch: prev as ObjPatch };
      }
      case 'clear': {
        const gone = [...this.objs.values()].filter((o) => o.layer === op.layer);
        if (gone.length === 0) {
          return undefined;
        }
        for (const o of gone) this.objs.delete(o.id);
        return { t: 'add', objs: gone };
      }
      case 'begin': {
        if (this.objs.has(op.obj.id) || this.objs.size >= LIMITS.maxObjects || !validObj(op.obj)) {
          return undefined;
        }
        this.objs.set(op.obj.id, cloneObj(op.obj));
        return null;
      }
      case 'pts': {
        const o = this.objs.get(op.id);
        if (!o || (o.kind !== 'pen' && o.kind !== 'marker') || op.p.length % 2 !== 0) {
          return undefined;
        }
        if ((o.pts.length + op.p.length) / 2 > LIMITS.maxPoints) {
          return undefined; // 상한 — 그 뒤 점은 버린다(획은 여기까지로 남는다)
        }
        o.pts.push(...op.p.map(q));
        return null;
      }
      case 'end': {
        // 문서 내용은 바뀌지 않는다(획은 이미 들어가 있다). 끝났다는 사실만 화면이 쓴다.
        return this.objs.has(op.id) ? { t: 'del', ids: [op.id] } : undefined;
      }
      case 'board': {
        if (this.board === op.on) {
          return undefined;
        }
        this.board = op.on;
        return { t: 'board', on: !op.on };
      }
    }
  }

  /** 전체 상태(재접속·창 재생성 `sync`). */
  snapshot(): { objs: DrawObj[]; board: boolean } {
    return { objs: this.all().map(cloneObj), board: this.board };
  }

  /** `sync` 로 받은 상태로 통째로 바꾼다. 상한·형식 밖 객체는 버린다. */
  load(s: { objs: DrawObj[]; board: boolean }): void {
    this.objs.clear();
    for (const o of s.objs.slice(0, LIMITS.maxObjects)) {
      if (validObj(o)) this.objs.set(o.id, cloneObj(o));
    }
    this.board = !!s.board;
    this.rev++;
  }

  reset(): void {
    this.objs.clear();
    this.board = false;
    this.rev++;
  }
}

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const ID_RE = /^[vr]\d{1,9}$/;
const inUnit = (v: unknown) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
const pair = (p: unknown) => Array.isArray(p) && p.length === 2 && inUnit(p[0]) && inUnit(p[1]);

/**
 * 객체 형식 검사 — 상대가 보낸 값은 **믿지 않는다**(채널 너머는 다른 프로그램이다).
 * 캔버스에 그대로 넘어가는 색 문자열은 `#rrggbb` 만 받는다(CSS 함수·이름 색 등으로 엉뚱한 값을 흘리지 않게).
 */
export function validObj(o: unknown): o is DrawObj {
  if (!o || typeof o !== 'object') return false;
  const x = o as Record<string, unknown>;
  if (typeof x.id !== 'string' || !ID_RE.test(x.id)) return false;
  if (x.by !== 'v' && x.by !== 'r') return false;
  if (x.id[0] !== x.by) return false; // id 접두사 = 작성자
  if (x.layer !== 'screen' && x.layer !== 'board') return false;
  if (typeof x.color !== 'string' || !COLOR_RE.test(x.color)) return false;
  if (typeof x.width !== 'number' || !(x.width >= 1 && x.width <= 200)) return false;
  switch (x.kind) {
    case 'pen':
    case 'marker':
      return (
        Array.isArray(x.pts) &&
        x.pts.length % 2 === 0 &&
        x.pts.length / 2 <= LIMITS.maxPoints &&
        (x.pts as unknown[]).every(inUnit)
      );
    case 'line':
    case 'arrow':
    case 'rect':
    case 'ellipse':
      return pair(x.a) && pair(x.b) && (x.fill === undefined || typeof x.fill === 'boolean');
    case 'text':
      return (
        pair(x.at) &&
        typeof x.text === 'string' &&
        x.text.length > 0 &&
        [...x.text].length <= LIMITS.maxTextChars
      );
    default:
      return false;
  }
}
